import { PushNotifications } from '@capacitor/push-notifications';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, signal, ElementRef, ViewChild, AfterViewInit, HostListener } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { Platform, MenuController, GestureController, AlertController } from '@ionic/angular';
import { NavController } from '@ionic/angular';
import { DataService } from './services/data.service';
import { GymService } from './services/gym.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
  showNotifications = signal<boolean>(false);
  currentUser: any = null;
  contentMarginTop = 56;
  showTrainers = signal<boolean>(false);

  @ViewChild('appHeader', { static: false }) headerRef?: ElementRef<HTMLElement>;
  @ViewChild('appToolbar', { static: false }) toolbarRef?: ElementRef<HTMLElement>;
  @ViewChild('notifBar', { static: false }) notifRef?: ElementRef<HTMLElement>;
  private resizeObserver?: ResizeObserver;
  private offsetRafId: number | null = null;

  constructor(
    private platform: Platform,
    private dataService: DataService,
    private gymService: GymService,
    private router: Router,
    private menuCtrl: MenuController,
    private gestureCtrl: GestureController,
    private navCtrl: NavController,
    private alertCtrl: AlertController,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private alertController: AlertController
  ) {
    this.platform.ready().then(() => {
      this.initializeFirebase();
      this.initializeGym();
    });
  }

  ngOnInit() {
    this.showTrainers.set(this.dataService.isAdmin() || this.dataService.isTrainer());

    if ('serviceWorker' in navigator) {
      // Registrar el SW en la raíz para que el scope cubra '/'
      navigator.serviceWorker.register('/firebase-messaging-sw.js')
        .then(function (registration) {
          console.log('Service Worker registrado con éxito:', registration);
          return navigator.serviceWorker.ready;
        })
        .then(function (registrationReady) {
          console.log('Service Worker ready. Controla la página:', !!navigator.serviceWorker.controller);
        })
        .catch(function (error) {
          console.log('Error al registrar el Service Worker:', error);
        });
    }

    this.dataService.user$.subscribe(async user => {
      this.currentUser = user;
      const hasNotif = !!user && (user.notification === true || (typeof user.notifications === 'boolean' && user.notifications === true));
      this.showNotifications.set(hasNotif);
      this.showTrainers.set(this.dataService.isAdmin() || this.dataService.isTrainer());
      this.scheduleUpdateContentOffset();
      this.refreshUnreadNotifications();
      if (user?.id) {
        try {
          await firstValueFrom(this.gymService.getCurrentGym());
        } catch (error) {
          console.error('Error al cargar el gym:', error);
        }
      }
    });

    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd))
      .subscribe(() => this.syncAuthFromStorage());

    this.syncAuthFromStorage();

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.refreshUnreadNotifications();
      }
    });

    this.platform.resume.subscribe(() => {
      this.refreshUnreadNotifications();
    });
  }

  private syncAuthFromStorage() {
    const token = this.dataService.getToken();
    const savedUser = this.dataService.getLocalStorage('user');
    const isValidSession = !!token && !this.dataService.isTokenExpired(token) && !!savedUser;
    const rawUrl =
      (typeof window !== 'undefined' && window.location?.pathname)
        ? `${window.location.pathname}${window.location.search ?? ''}`
        : (this.router.url || '');
    const url = rawUrl.length > 1 && rawUrl.endsWith('/') ? rawUrl.slice(0, -1) : rawUrl;
    const publicPrefixes = [
      '/tabs/home',
      '/tabs/courses',
      '/tabs/course-detail/',
      '/tabs/horario',
      '/tabs/tarifas',
      '/tabs/trainers',
      '/tabs/trainer-profile/',
      '/tabs/rules',
      '/tabs/info',
      '/tabs/info_event',
      '/tabs/notifications',
      '/tabs/signin',
      '/tabs/signup',
      '/tabs/forgot-password',
      '/tabs/new-account',
      '/signin',
      '/signup',
      '/forgot-password',
      '/new-account',
      '/rules',
      '/privacy-policy',
      '/stripe/privacy-policy',
      '/stripe/payment-success',
      '/stripe/payment-error',
    ];
    const isPublic = url === '/' || publicPrefixes.some((p) => url.startsWith(p));

    this.ngZone.run(() => {
      if (isValidSession) {
        this.currentUser = savedUser;
        const hasNotif = !!savedUser && (savedUser.notification === true || (typeof savedUser.notifications === 'boolean' && savedUser.notifications === true));
        this.showNotifications.set(hasNotif);
        if (!this.dataService.getUser() || this.dataService.getUser()?.id !== savedUser?.id) {
          this.dataService.updateUser(savedUser);
        }
      } else {
        this.currentUser = null;
        this.showNotifications.set(false);
        if (this.dataService.getUser()) {
          this.dataService.updateUser(null);
        }
        if (!isPublic) {
          this.router.navigate(['/tabs/signin']);
        }
      }
      this.showTrainers.set(this.dataService.isAdmin() || this.dataService.isTrainer());
        this.scheduleUpdateContentOffset();
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.scheduleUpdateContentOffset());
    this.setupResizeObserver();
    const outlet = document.getElementById('main-content') as HTMLElement | null;
    if (outlet) {
      let edge = false;
      const gesture = this.gestureCtrl.create({
        el: outlet,
        gestureName: 'swipe-back',
        direction: 'x',
        threshold: 10,
        onStart: (ev) => { edge = (ev as any).startX < 30; },
        onEnd: (ev) => {
          const dx = (ev as any).deltaX || 0;
          const dy = Math.abs((ev as any).deltaY || 0);
          const canBack = this.showBackButton();
          this.menuCtrl.isOpen().then((isOpen) => {
            if (edge && canBack && !isOpen && dx > 100 && dy < 80) {
              this.navCtrl.back();
            }
          });
        },
      });
      gesture.enable(true);
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.scheduleUpdateContentOffset();
  }

  private updateContentOffset() {
    const headerH = this.headerRef?.nativeElement?.getBoundingClientRect().height ?? (this.toolbarRef?.nativeElement?.getBoundingClientRect().height ?? 56);
    const notifH = this.showNotifications() && this.notifRef?.nativeElement ? this.notifRef.nativeElement.getBoundingClientRect().height : 0;
    const extra = 2;
    this.contentMarginTop = headerH + notifH + extra;
    document.documentElement.style.setProperty('--app-offset-top', `${this.contentMarginTop}px`);
  }

  private scheduleUpdateContentOffset() {
    if (this.offsetRafId != null) {
      cancelAnimationFrame(this.offsetRafId);
    }
    this.offsetRafId = requestAnimationFrame(() => {
      this.updateContentOffset();
      requestAnimationFrame(() => this.updateContentOffset());
      setTimeout(() => this.updateContentOffset(), 0);
      setTimeout(() => this.updateContentOffset(), 120);
      this.offsetRafId = null;
    });
  }

  private setupResizeObserver() {
    if (typeof (window as any).ResizeObserver !== 'function') return;
    if (this.resizeObserver) return;
    const ro = new ResizeObserver(() => this.scheduleUpdateContentOffset());
    if (this.headerRef?.nativeElement) ro.observe(this.headerRef.nativeElement);
    if (this.toolbarRef?.nativeElement) ro.observe(this.toolbarRef.nativeElement);
    if (this.notifRef?.nativeElement) ro.observe(this.notifRef.nativeElement);
    this.resizeObserver = ro;
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    if (this.offsetRafId != null) {
      cancelAnimationFrame(this.offsetRafId);
      this.offsetRafId = null;
    }
  }

  initializeFirebase() {
    const firebaseConfig = {
      apiKey: "AIzaSyAM9CpoLairX6IWZ8Wd_nCa1yorbES5rzU",
      authDomain: "panthera-9ffb1.firebaseapp.com",
      projectId: "panthera-9ffb1",
      storageBucket: "panthera-9ffb1.firebasestorage.app",
      messagingSenderId: "759038148820",
      appId: "1:759038148820:web:07cd95132c2facbdbebb41",
    };
    const app = initializeApp(firebaseConfig);
    const messaging = getMessaging(app);
    this.requestNotificationPermission(messaging);
    this.listenForForegroundMessages(messaging);
  }

  requestNotificationPermission(messaging: any) {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        this.getToken(messaging);
      }
    }).catch((error) => {
      console.error('Error al solicitar permiso de notificación:', error);
    });
  }

  getToken(messaging: any) {
    getToken(messaging, {
      vapidKey: 'BBz4FObhEztep5ER8l7ORc7uV5o8ycJCzynxlgOmaYPPcymBpQNiYJ1OvDm40R8tJaEJp8HELERtFAoEa-OR89U',
    })
      .then((currentToken: string) => {
        if (currentToken) {
          this.dataService._tokenFCM = currentToken;
        }
      })
      .catch((error) => {
        console.error('Error al obtener el token:', error);
      });
  }

  listenForForegroundMessages(messaging: any) {
    onMessage(messaging, (payload) => {
      const url = this.router.url;
      const inChat = /\/social-chat\/(?!$)([a-f0-9-]+)/i.test(url);
      if (!inChat) {
        const notificationTitle = payload?.notification?.title;
        if (notificationTitle) {
          const notificationOptions = {
            body: payload?.notification?.body,
            icon: payload?.notification?.icon,
          };
          new Notification(notificationTitle, notificationOptions);
        }
      }
      this.setNotificationsFlag(true);
    });
  }

  private setNotificationsFlag(value: boolean) {
    if (!this.currentUser) {
      this.showNotifications.set(false);
      this.scheduleUpdateContentOffset();
      return;
    }
    const currFlag = this.currentUser.notification === true || (typeof this.currentUser.notifications === 'boolean' && this.currentUser.notifications === true);
    if (currFlag !== value) {
      this.currentUser.notification = value;
      if (!Array.isArray(this.currentUser.notifications)) {
        this.currentUser.notifications = value;
      }
      this.dataService.updateUser(this.currentUser);
      this.dataService.setLocalStorage('user', this.currentUser);
    }
    this.showNotifications.set(value);
    this.scheduleUpdateContentOffset();
  }

  private async refreshUnreadNotifications() {
    if (!this.currentUser?.id) {
      this.showNotifications.set(false);
      this.scheduleUpdateContentOffset();
      return;
    }
    try {
      const serverUser: any = await firstValueFrom(
        this.dataService.getUserServerData(this.currentUser.id),
      );
      const hasNotif = !!serverUser && (serverUser.notification === true || (typeof serverUser.notifications === 'boolean' && serverUser.notifications === true));
      this.setNotificationsFlag(hasNotif);
    } catch {
      this.scheduleUpdateContentOffset();
    }
  }

  // Navegación
  goToNotifications() {
    if (this.currentUser) {
      this.setNotificationsFlag(false);
    }
    this.router.navigate(['/tabs/notifications']);
  }

  goToProfile() {
    this.router.navigate(['/tabs/profile']);
  }

  goToLogin() {
    this.menuCtrl.close();
    this.router.navigate(['/tabs/signin']);
  }

  isCalendarAccess() {
    return !!this.currentUser;
  }

  showBackButton(): boolean {
    const url = this.router.url || '';
    return !url.startsWith('/tabs/home');
  }

  appBack() {
    if (window.history.length > 1) {
      this.navCtrl.back();
    } else {
      this.router.navigate(['/tabs/home']);
    }
  }
  async confirmLogout() {
    const alert = await this.alertCtrl.create({
      header: 'Cerrar sesión',
      message: '¿Seguro que quieres cerrar sesión?',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        {
          text: 'Salir', role: 'destructive', handler: () => {
            this.dataService.logout();
            this.menuCtrl.close();
            this.router.navigate(['/tabs/signin']);
          }
        }
      ]
    });
    await alert.present();
  }

  async deleteAccount() {
    const alert = await this.alertController.create({
      header: 'Eliminar cuenta',
      message: 'Esta acción desactivará tu cuenta de forma permanente. ¿Deseas continuar?',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
        },
        {
          text: 'Eliminar',
          role: 'destructive',
          handler: async () => {
            const response = await this.dataService.deleteAccountLogical();
            if (!response.error) {
              this.dataService.logout();
              this.router.navigate(['/tabs/home']);
              return;
            }
            const errorAlert = await this.alertController.create({
              header: 'No se pudo eliminar la cuenta',
              message: 'Inténtalo de nuevo en unos minutos.',
              buttons: ['Aceptar'],
            });
            await errorAlert.present();
          },
        },
      ],
    });
    await alert.present();
  }

  private async initializeGym(): Promise<void> {
    // Sin sesión no hay gimnasio asociado al usuario: se resuelve al iniciar
    // sesión (vía user$) para no quedarnos con el gimnasio por defecto.
    const token = this.dataService.getToken();
    if (!token || this.dataService.isTokenExpired(token)) return;
    try {
      await firstValueFrom(this.gymService.getCurrentGym());
    } catch (error) {
      console.error('Error al cargar el gym:', error);
    }
  }
}
